import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { seoConfig } from '../utils/seoConfig';

const NotFound = () => {
  useEffect(() => {
    // Track 404 errors for SEO analysis
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: '404 - Page Not Found',
        page_location: window.location.href,
        custom_parameter_1: '404_error'
      });
    }

    // Log 404 for debugging
    console.warn(`404 Error - Page not found: ${window.location.pathname}`);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Page Not Found - QuantumFrame Studio",
    "description": "The page you're looking for cannot be found. Return to QuantumFrame Studio's homepage for web development and digital marketing services in Bhopal.",
    "url": window.location.href,
    "isPartOf": {
      "@type": "WebSite",
      "name": seoConfig.siteName,
      "url": seoConfig.baseUrl
    },
    "mainEntity": {
      "@type": "Thing",
      "name": "404 Error",
      "description": "Page not found error"
    }
  };

  return (
    <div className="not-found-container" style={containerStyle}>
      <Helmet>
        <title>404 - Page Not Found | QuantumFrame Studio</title>
        <meta 
          name="description" 
          content="The page you're looking for cannot be found. Return to QuantumFrame Studio's homepage for web development and digital marketing services in Bhopal." 
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`${seoConfig.baseUrl}/404`} />
        
        {/* Open Graph for 404 pages */}
        <meta property="og:title" content="Page Not Found | QuantumFrame Studio" />
        <meta property="og:description" content="The page you're looking for cannot be found. Explore our web development and digital marketing services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={`${seoConfig.baseUrl}/assets/images/404-error-page.jpg`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Page Not Found | QuantumFrame Studio" />
        <meta name="twitter:description" content="The page you're looking for cannot be found." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div style={contentStyle}>
        {/* 404 Visual */}
        <div style={numberStyle}>404</div>
        
        {/* Error Message */}
        <h1 style={titleStyle}>Oops! Page Not Found</h1>
        <p style={descriptionStyle}>
          The page you're looking for seems to have vanished into the digital void. 
          Don't worry, even the best developers encounter broken links sometimes!
        </p>
        
        {/* Helpful Links */}
        <div style={linksStyle}>
          <Link to="/" style={linkStyle} className="primary-link">
            🏠 Return Home
          </Link>
          <Link to="/services" style={linkStyle} className="secondary-link">
            🚀 Our Services
          </Link>
          <Link to="/contact" style={linkStyle} className="secondary-link">
            📞 Contact Us
          </Link>
        </div>
        
        {/* Search Suggestion */}
        <div style={searchSectionStyle}>
          <h3 style={searchTitleStyle}>Looking for something specific?</h3>
          <p style={searchDescStyle}>Try these popular pages:</p>
          <ul style={popularLinksStyle}>
            <li><Link to="/services" style={popularLinkStyle}>Web Development Services</Link></li>
            <li><Link to="/services" style={popularLinkStyle}>Digital Marketing Solutions</Link></li>
            <li><Link to="/services" style={popularLinkStyle}>SEO Services in Bhopal</Link></li>
            <li><Link to="/testimonials" style={popularLinkStyle}>Client Testimonials</Link></li>
            <li><Link to="/about-us" style={popularLinkStyle}>About QuantumFrame Studio</Link></li>
          </ul>
        </div>
        
        {/* Contact Information */}
        <div style={contactSectionStyle}>
          <h3 style={contactTitleStyle}>Need Help?</h3>
          <p style={contactDescStyle}>
            Our team is here to help you find what you're looking for.
          </p>
          <div style={contactInfoStyle}>
            <a href={`tel:${seoConfig.business.phone}`} style={contactLinkStyle}>
              📞 {seoConfig.business.phone}
            </a>
            <a href={`mailto:${seoConfig.business.email}`} style={contactLinkStyle}>
              ✉️ {seoConfig.business.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  color: '#ffffff',
  textAlign: 'center',
  padding: '20px'
};

const contentStyle = {
  maxWidth: '600px',
  width: '100%'
};

const numberStyle = {
  fontSize: 'clamp(80px, 15vw, 200px)',
  fontWeight: '900',
  lineHeight: '1',
  marginBottom: '20px',
  background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textShadow: '0 4px 8px rgba(0,0,0,0.3)'
};

const titleStyle = {
  fontSize: 'clamp(24px, 5vw, 48px)',
  fontWeight: '700',
  marginBottom: '16px',
  color: '#ffffff'
};

const descriptionStyle = {
  fontSize: '18px',
  lineHeight: '1.6',
  marginBottom: '40px',
  opacity: '0.9'
};

const linksStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '15px',
  justifyContent: 'center',
  marginBottom: '50px'
};

const linkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 24px',
  borderRadius: '50px',
  textDecoration: 'none',
  fontWeight: '600',
  fontSize: '16px',
  transition: 'all 0.3s ease',
  background: 'rgba(255,255,255,0.2)',
  color: '#ffffff',
  border: '2px solid rgba(255,255,255,0.3)',
  backdropFilter: 'blur(10px)'
};

const searchSectionStyle = {
  marginBottom: '40px',
  padding: '30px',
  background: 'rgba(255,255,255,0.1)',
  borderRadius: '20px',
  backdropFilter: 'blur(10px)'
};

const searchTitleStyle = {
  fontSize: '24px',
  fontWeight: '600',
  marginBottom: '16px',
  color: '#ffffff'
};

const searchDescStyle = {
  fontSize: '16px',
  marginBottom: '20px',
  opacity: '0.8'
};

const popularLinksStyle = {
  listStyle: 'none',
  padding: '0',
  margin: '0'
};

const popularLinkStyle = {
  color: '#ffffff',
  textDecoration: 'none',
  fontSize: '16px',
  padding: '8px 0',
  display: 'block',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  transition: 'color 0.3s ease'
};

const contactSectionStyle = {
  padding: '30px',
  background: 'rgba(255,255,255,0.1)',
  borderRadius: '20px',
  backdropFilter: 'blur(10px)'
};

const contactTitleStyle = {
  fontSize: '24px',
  fontWeight: '600',
  marginBottom: '16px',
  color: '#ffffff'
};

const contactDescStyle = {
  fontSize: '16px',
  marginBottom: '20px',
  opacity: '0.8'
};

const contactInfoStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'center'
};

const contactLinkStyle = {
  color: '#ffffff',
  textDecoration: 'none',
  fontSize: '16px',
  padding: '8px 16px',
  borderRadius: '25px',
  background: 'rgba(255,255,255,0.2)',
  transition: 'background 0.3s ease'
};

export default NotFound;